import { WplywContext } from "../../../../../context/wplywContext";
import { useContext, useRef, useState } from "react";
import ProgressBarReckonings from "./ProgressBarReckonings";
import ProgressBarDeductions from "./ProgressBarDeductions";
import SinglePerson from "./SinglePerson";
import { Button, message } from "antd";
import LineSpacer from "../../../../../components/LineSpacer";
import { useAuth } from "../../../../../context/AuthContext";
import axios from "../../../../../context/axiosConfig";

const StagePersons = () => {
	const { wplyw, setWplyw } = useContext(WplywContext);
	const isDisabled = wplyw.mode === "preview"; // Disable inputs if mode is "preview"
	const personRefs = useRef({}); // Ref to track each person's element by ID
	const { user } = useAuth();
	const [importingFromMso, setImportingFromMso] = useState(false);

	const wplywData = wplyw.wplyw_data.data.msr_wplywy;
	const persons = wplywData.msr_persons || [];
	const defaultDeductionType =
		wplyw.wplyw_data.data.default_values?.msr_reckonings_types?.[0]
			?.value || null;

	const netAmount = Number(
		(wplywData.msr_wplywy_primary_amount || 0) -
			(wplywData.msr_wplywy_primary_deduction || 0)
	);
	const usedAmounts = persons.reduce(
		(sum, person) => sum + Number(person.msr_persons_amount || 0),
		0
	);
	const totalDeduction = Number(wplywData.msr_wplywy_primary_deduction || 0);
	const usedDeduction = persons.reduce(
		(sum, person) => sum + Number(person.msr_persons_deduction || 0),
		0
	);
	const amountDiff = Math.abs(netAmount - usedAmounts);
	const deductionDiff = Math.abs(totalDeduction - usedDeduction);
	const reachedLimits = amountDiff < 0.01 && deductionDiff < 0.01;

	const createBasePerson = () => {
		const newPersonId = "temp_" + Date.now();
		const personOwnId = Math.floor(new Date().getTime() / 1000).toString();
		const remainingAmount = parseFloat(
			(netAmount - usedAmounts).toFixed(2)
		);
		const remainingDeduction = parseFloat(
			(totalDeduction - usedDeduction).toFixed(2)
		);

		return {
			msr_persons_id: newPersonId,
			msr_persons_own_id: personOwnId,
			msr_persons_name: "",
			msr_persons_lastname: "",
			msr_persons_pesel: "",
			msr_persons_amount:
				persons.length === 0
					? parseFloat(netAmount.toFixed(2))
					: remainingAmount,
			msr_persons_deduction:
				persons.length === 0 ? totalDeduction : remainingDeduction,
			msr_persons_majority: 1,
			msr_persons_deduction_document: "",
			msr_persons_deduction_comment: "",
			msr_persons_deduction_type: defaultDeductionType,
			msr_persons_comment: "",
			msr_persons_commission: null,
			msr_persons_agentid: null,
			msr_reckonings: [],
			msr_transfers: [],
		};
	};

	const appendPerson = (person, changeLabel) => {
		setWplyw((prevWplyw) => {
			const currentPersons =
				prevWplyw.wplyw_data.data.msr_wplywy.msr_persons || [];
			const updatedPersons = [...currentPersons, person];

			const changes = [
				...(prevWplyw.changes || []),
				{
					etap: "Osoby",
					field: "msr_persons",
					label: changeLabel,
					action: "add",
					oldValue: null,
					newValue: `Dodano nową osobę o ID ${person.msr_persons_own_id}`,
					datetime: Math.floor(new Date().getTime() / 1000),
					user: user.user.mlg_users_id,
					objectid: person.msr_persons_own_id,
					wplywid:
						prevWplyw.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
					personid: person.msr_persons_own_id,
				},
			];

			return {
				...prevWplyw,
				wplyw_data: {
					...prevWplyw.wplyw_data,
					data: {
						...prevWplyw.wplyw_data.data,
						msr_wplywy: {
							...prevWplyw.wplyw_data.data.msr_wplywy,
							msr_persons: updatedPersons,
						},
					},
				},
				changes,
			};
		});

		setTimeout(() => {
			if (personRefs.current[person.msr_persons_id]) {
				personRefs.current[person.msr_persons_id].scrollIntoView({
					behavior: "smooth",
				});
			}
		}, 100);
	};

	const handleAddPerson = () => {
		const newPerson = createBasePerson();
		appendPerson(newPerson, "Nowa osoba");
	};

	const handleImportFromMso = async () => {
		const contractId = wplywData.msr_wplywy_mso_contract_id;
		if (!contractId) {
			message.warning("Najpierw wybierz umowę z MSO.");
			return;
		}

		if (persons.length > 0) {
			message.warning(
				"Import z MSO jest dostępny tylko dla pierwszej osoby."
			);
			return;
		}

		try {
			setImportingFromMso(true);
			const response = await axios.get(
				`/wplyw/mso/contracts/${contractId}`
			);

			if (!response.data?.status) {
				message.error(
					response.data?.message ||
						"Nie udało się pobrać danych umowy z MSO."
				);
				return;
			}

			const details = response.data?.data;
			if (!details) {
				message.error("Brak danych umowy przekazanych przez MSO.");
				return;
			}

			const newPerson = createBasePerson();
			newPerson.msr_persons_name = details.clientFirstName || "";
			newPerson.msr_persons_lastname = details.clientLastName || "";
			newPerson.msr_persons_pesel = details.clientPesel || "";
			newPerson.msr_persons_majority = details.isClientIncapacitated
				? 0
				: 1;
			const agentId = details.signingAgentMshUserId;
			if (agentId === null || agentId === undefined || agentId === "") {
				newPerson.msr_persons_agentid = null;
			} else {
				const parsedAgentId =
					typeof agentId === "number"
						? agentId
						: parseInt(agentId, 10);
				newPerson.msr_persons_agentid = Number.isNaN(parsedAgentId)
					? null
					: parsedAgentId;
			}
			newPerson.msr_persons_commission =
				details.marshallCommission !== undefined &&
				details.marshallCommission !== null
					? Number(details.marshallCommission)
					: null;

			appendPerson(newPerson, "Osoba z MSO");
			message.success("Dodano osobę na podstawie danych z MSO.");
		} catch (error) {
			console.error(error);
			const errorMessage =
				error?.response?.data?.message ||
				"Nie udało się pobrać danych umowy z MSO.";
			message.error(errorMessage);
		} finally {
			setImportingFromMso(false);
		}
	};
	return (
		<div
			className="formwithpreview"
			style={{ display: "flex", flexDirection: "column", gap: "16px" }}
		>
			<ProgressBarReckonings />
			<ProgressBarDeductions />
			<div
				style={{
					gap: "20px",
					alignItems: "center",
					display: isDisabled ? "none" : "flex",
				}}
			>
				<Button
					style={{ fontSize: "16px", height: "auto" }}
					onClick={handleImportFromMso}
					loading={importingFromMso}
					disabled={
						isDisabled ||
						importingFromMso ||
						!wplywData.msr_wplywy_mso_contract_id ||
						persons.length > 0
					}
				>
					Dodaj osobę z MSO
				</Button>
				<LineSpacer />
				<Button
					type="primary"
					style={{ fontSize: "16px", height: "auto" }}
					onClick={handleAddPerson}
					disabled={isDisabled || reachedLimits}
				>
					Dodaj osobę {reachedLimits ? " - LIMIT KWOT" : ""}
				</Button>
			</div>
			{/* Render SinglePerson components for each person */}
			{wplyw?.wplyw_data?.data?.msr_wplywy.msr_persons?.map(
				(person, index) => (
					<div
						key={person.msr_persons_id}
						id={`person-${person.msr_persons_id}`} // Set ID for easy identification
						ref={(el) =>
							(personRefs.current[person.msr_persons_id] = el)
						} // Assign each person to the ref
					>
						<SinglePerson
							index={index + 1}
							personId={person.msr_persons_id}
						/>
					</div>
				)
			)}
		</div>
	);
};

export default StagePersons;
